'use client'

import { useEffect, useRef, useState } from 'react'

interface UseIntersectionOptions {
  threshold?: number | number[]
  rootMargin?: string
  triggerOnce?: boolean
  delay?: number
}

export function useIntersection<T extends HTMLElement = HTMLDivElement>({
  threshold = 0.1,
  rootMargin = '0px',
  triggerOnce = true,
  delay = 0,
}: UseIntersectionOptions = {}) {
  const [isIntersecting, setIsIntersecting] = useState(false)
  const [hasIntersected, setHasIntersected] = useState(false)
  const elementRef = useRef<T>(null)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isIntersect = entry.isIntersecting
        setIsIntersecting(isIntersect)

        if (isIntersect && !hasIntersected) {
          if (delay > 0) {
            setTimeout(() => {
              setHasIntersected(true)
            }, delay)
          } else {
            setHasIntersected(true)
          }
        }

        if (!triggerOnce && !isIntersect && hasIntersected) {
          setHasIntersected(false)
        }
      },
      {
        threshold,
        rootMargin,
      },
    )

    observer.observe(element)

    return () => {
      observer.unobserve(element)
    }
  }, [threshold, rootMargin, triggerOnce, delay, hasIntersected])

  return {
    elementRef,
    isIntersecting,
    hasIntersected,
  }
}
